import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { ScheduledMessage } from '../services/conversationService';
import { formatDateTime, formatDateTimeInputValue, parseDateTimeInputValue } from '../../../shared/utils/localization';
import { getQuotedMessageSnippet, truncateMessageSnippet } from '../utils/messageUtils';
import styles from './ConversationDetails.module.css';

interface ScheduledMessagesPanelProps {
	items: ScheduledMessage[];
	error: string | null;
	onCancel: (scheduledMessageId: string) => Promise<void>;
	onReschedule: (scheduledMessageId: string, scheduledAt: Date, content?: string) => Promise<void>;
	onJumpToQuotedMessage: (messageId?: string | null) => void;
}

const toLocalDateTimeInputValue = (value: Date): string => formatDateTimeInputValue(value);

const buildLaterTodayPreset = (): Date => {
	const now = new Date();
	const preset = new Date(now);
	preset.setHours(13, 0, 0, 0);
	if (preset.getTime() <= now.getTime()) {
		preset.setDate(preset.getDate() + 1);
	}
	return preset;
};

const buildMondayMorningPreset = (): Date => {
	const now = new Date();
	const day = now.getDay();
	const daysUntilMonday = (8 - day) % 7 || 7;
	const preset = new Date(now);
	preset.setDate(now.getDate() + daysUntilMonday);
	preset.setHours(9, 0, 0, 0);
	return preset;
};

const renderScheduledAttachmentIcon = (kind: 'audio' | 'video' | 'image' | 'document' | 'attachment') => {
	if (kind === 'audio') {
		return (
			<svg className={styles.scheduledAttachmentIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
				<path d="M6 10v4h3l4 3V7l-4 3H6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
				<path d="M16.5 9.5a4.5 4.5 0 0 1 0 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
			</svg>
		);
	}

	if (kind === 'video') {
		return (
			<svg className={styles.scheduledAttachmentIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
				<rect x="3.5" y="6" width="12" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
				<path d="m15.5 10.2 5-2v7.6l-5-2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
			</svg>
		);
	}

	if (kind === 'image') {
		return (
			<svg className={styles.scheduledAttachmentIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
				<rect x="3.5" y="4.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
				<circle cx="9" cy="10" r="1.7" fill="currentColor" />
				<path d="m6.5 16 3.5-3 2.6 2.2 2.8-2.5 2.1 3.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
		);
	}

	if (kind === 'document') {
		return (
			<svg className={styles.scheduledAttachmentIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
				<path d="M7 3.5h7l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V5A1.5 1.5 0 0 1 7.5 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
				<path d="M14 3.5V8h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
				<path d="M9 12h6M9 15h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
			</svg>
		);
	}

	return (
		<svg className={styles.scheduledAttachmentIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
			<path d="m14 7-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
			<path d="M11.5 9.5 16 14a3 3 0 0 1-4.2 4.2l-5.3-5.3a4.5 4.5 0 1 1 6.4-6.4l4.3 4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
};

const ScheduledMessagesPanel: React.FC<ScheduledMessagesPanelProps> = ({
	items,
	error,
	onCancel,
	onReschedule,
	onJumpToQuotedMessage,
}) => {
	const { t } = useTranslation(['conversations', 'common']);
	const panelRef = React.useRef<HTMLElement | null>(null);
	const menuRef = React.useRef<HTMLDivElement | null>(null);
	const [editingMessageId, setEditingMessageId] = React.useState<string | null>(null);
	const [showCustomScheduleInput, setShowCustomScheduleInput] = React.useState(false);
	const [customScheduleValue, setCustomScheduleValue] = React.useState('');
	const [editContent, setEditContent] = React.useState('');
	const [isRescheduling, setIsRescheduling] = React.useState(false);
	const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);

	const editingItem = React.useMemo(
		() => items.find(item => item.id === editingMessageId) ?? null,
		[items, editingMessageId]
	);

	React.useEffect(() => {
		if (!editingMessageId) {
			return;
		}

		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) {
				return;
			}

			const clickedEditor = target instanceof Element ? target.closest('[data-scheduled-editor="true"]') : null;
			if (clickedEditor) {
				return;
			}

			setEditingMessageId(null);
			setShowCustomScheduleInput(false);
			setEditContent('');
			setMenuPosition(null);
		};

		const handleViewportChange = () => {
			setEditingMessageId(null);
			setShowCustomScheduleInput(false);
			setEditContent('');
			setMenuPosition(null);
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('touchstart', handlePointerDown);
		window.addEventListener('resize', handleViewportChange);
		window.addEventListener('scroll', handleViewportChange, true);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('touchstart', handlePointerDown);
			window.removeEventListener('resize', handleViewportChange);
			window.removeEventListener('scroll', handleViewportChange, true);
		};
	}, [editingMessageId]);

	React.useEffect(() => {
		if (!menuPosition || !menuRef.current) {
			return;
		}

		const rect = menuRef.current.getBoundingClientRect();
		const margin = 12;
		let nextTop = menuPosition.top;
		let nextLeft = menuPosition.left;

		if (rect.right > window.innerWidth - margin) {
			nextLeft -= rect.right - (window.innerWidth - margin);
		}
		if (rect.left < margin) {
			nextLeft += margin - rect.left;
		}
		if (rect.bottom > window.innerHeight - margin) {
			nextTop -= rect.bottom - (window.innerHeight - margin);
		}
		if (rect.top < margin) {
			nextTop += margin - rect.top;
		}

		if (nextTop !== menuPosition.top || nextLeft !== menuPosition.left) {
			setMenuPosition({ top: nextTop, left: nextLeft });
		}
	}, [menuPosition]);

	const handleOpenEditor = (item: ScheduledMessage, anchor: HTMLButtonElement) => {
		if (editingMessageId === item.id) {
			setEditingMessageId(null);
			setShowCustomScheduleInput(false);
			setEditContent('');
			setMenuPosition(null);
			return;
		}

		const rect = anchor.getBoundingClientRect();
		const menuWidth = 330;
		const margin = 12;
		const left = Math.min(
			Math.max(margin, rect.right - menuWidth),
			window.innerWidth - menuWidth - margin
		);
		const top = Math.max(margin, rect.top - 8);

		setEditingMessageId(item.id);
		setShowCustomScheduleInput(false);
		setEditContent(item.content ?? '');
		setMenuPosition({ top, left });
		const existingDate = new Date(item.scheduledAtUtc);
		setCustomScheduleValue(toLocalDateTimeInputValue(Number.isNaN(existingDate.getTime()) ? new Date(Date.now() + 30 * 60 * 1000) : existingDate));
	};

	const handleApplyReschedule = async (scheduledMessageId: string, date: Date) => {
		if (Number.isNaN(date.getTime())) {
			return;
		}

		setIsRescheduling(true);
		try {
			await onReschedule(scheduledMessageId, date, editContent);
			setEditingMessageId(null);
			setShowCustomScheduleInput(false);
			setEditContent('');
			setMenuPosition(null);
		} finally {
			setIsRescheduling(false);
		}
	};

	const handleSaveChanges = async () => {
		if (!editingItem) {
			return;
		}

		const nextDate = showCustomScheduleInput && customScheduleValue
			? parseDateTimeInputValue(customScheduleValue)
			: new Date(editingItem.scheduledAtUtc);

		if (!nextDate || Number.isNaN(nextDate.getTime())) {
			return;
		}

		await handleApplyReschedule(editingItem.id, nextDate);
	};

	return (
		<section ref={panelRef} className={styles.scheduledPanel} aria-label={t('scheduledMessages')}>
			<div className={styles.scheduledPanelHeader}>
				<h3 className={styles.scheduledPanelTitle}>{t('scheduledMessages')}</h3>
			</div>

			{error && <p className={styles.messageErrorText}>{error}</p>}

			{items.length === 0 ? (
				<p className={styles.scheduledEmpty}>{t('noScheduledMessages')}</p>
			) : (
				<div className={styles.scheduledList}>
					{items.map(item => (
						(() => {
							const quotedSenderName = item.quotedMessage?.sender.name ?? item.quotedMessage?.sender.id;
							const quotedSnippet = item.quotedMessage ? truncateMessageSnippet(getQuotedMessageSnippet(item.quotedMessage), 120) : null;

							return (
						<article
							key={item.id}
							className={`${styles.scheduledCard} ${editingMessageId === item.id ? styles.scheduledCardActive : ''}`}
						>
							<div className={styles.scheduledBody}>
								{item.quotedMessage && quotedSenderName && quotedSnippet ? (
									<button
										type="button"
										className={styles.scheduledQuotedMessagePreview}
										onClick={() => onJumpToQuotedMessage(item.quotedMessageId)}
									>
										<span className={styles.scheduledQuotedMessageLabel}>{t('replyingTo', { name: quotedSenderName })}</span>
										<span className={styles.scheduledQuotedMessageText}>{quotedSnippet}</span>
									</button>
								) : null}
								<p className={styles.scheduledPreview} title={item.content || t('noContent')}>{item.content || t('noContent')}</p>
								<div className={styles.scheduledFooter}>
									<div className={styles.scheduledMeta}>
										{item.mediaUrl && (
											<div className={styles.scheduledAttachmentRow}>
												{item.mimeType?.startsWith('audio') && (
													<span className={styles.scheduledAttachmentBadge} title={t('audioAttachment')}>
														<span className={styles.scheduledAttachmentIcon}>{renderScheduledAttachmentIcon('audio')}</span> {t('audioAttachment')}
													</span>
												)}
												{item.mimeType?.startsWith('video') && (
													<span className={styles.scheduledAttachmentBadge} title={t('videoAttachment')}>
														<span className={styles.scheduledAttachmentIcon}>{renderScheduledAttachmentIcon('video')}</span> {t('videoAttachment')}
													</span>
												)}
												{item.mimeType?.startsWith('image') && (
													<span className={styles.scheduledAttachmentBadge} title={t('imageAttachment')}>
														<span className={styles.scheduledAttachmentIcon}>{renderScheduledAttachmentIcon('image')}</span> {t('imageAttachment')}
													</span>
												)}
												{item.mimeType?.startsWith('application') && (
													<span className={styles.scheduledAttachmentBadge} title={t('documentAttachment')}>
														<span className={styles.scheduledAttachmentIcon}>{renderScheduledAttachmentIcon('document')}</span> {item.fileName || t('documentAttachment')}
													</span>
												)}
												{!item.mimeType && item.fileName && (
													<span className={styles.scheduledAttachmentBadge} title={t('attachment')}>
														<span className={styles.scheduledAttachmentIcon}>{renderScheduledAttachmentIcon('attachment')}</span> {item.fileName}
													</span>
												)}
											</div>
										)}
										<small className={styles.scheduledTime}>{t('scheduledFor', { timestamp: formatDateTime(item.scheduledAtUtc) })}</small>
									</div>
									<div className={styles.scheduledActions}>
										<div className={styles.scheduledEditWrap} data-scheduled-editor="true">
											<button
												type="button"
												className={styles.scheduledEditBtn}
												onClick={event => {
													handleOpenEditor(item, event.currentTarget);
												}}
												disabled={isRescheduling}
											>
												{t('edit', { ns: 'common' })}
											</button>
										</div>
										<button
											type="button"
											className={styles.scheduledCancelBtn}
											disabled={isRescheduling}
											onClick={() => {
												void onCancel(item.id);
											}}
										>
											{t('cancel', { ns: 'common' })}
										</button>
									</div>
								</div>
							</div>
						</article>
							);
						})()
					))}
				</div>
			)}

			{editingItem && menuPosition && createPortal(
				<div
					ref={menuRef}
					className={`${styles.scheduleMenu} ${styles.scheduleMenuFloating}`}
					style={{ top: menuPosition.top, left: menuPosition.left }}
					data-scheduled-editor="true"
				>
					<div className={styles.scheduleMenuTitle}>{t('scheduleMessage')}</div>
						<div className={styles.scheduleEditorSection}>
							<label className={styles.scheduleEditorLabel} htmlFor="scheduled-message-content">
								{t('message')}
							</label>
							<textarea
								id="scheduled-message-content"
								className={styles.scheduledTextarea}
								value={editContent}
								onChange={event => setEditContent(event.target.value)}
								rows={4}
								disabled={isRescheduling}
								placeholder={t('editScheduledMessageText')}
							/>
						</div>
						<div className={styles.scheduleMenuDivider} />
					<button
						type="button"
						className={styles.scheduleMenuItem}
						disabled={isRescheduling}
						onClick={() => {
							void handleApplyReschedule(editingItem.id, buildLaterTodayPreset());
						}}
					>
						{t('laterTodayPreset')}
					</button>
					<button
						type="button"
						className={styles.scheduleMenuItem}
						disabled={isRescheduling}
						onClick={() => {
							void handleApplyReschedule(editingItem.id, buildMondayMorningPreset());
						}}
					>
						{t('mondayMorningPreset')}
					</button>
					<div className={styles.scheduleMenuDivider} />
					<button
						type="button"
						className={styles.scheduleMenuItem}
						disabled={isRescheduling}
						onClick={() => setShowCustomScheduleInput(true)}
					>
						{t('customTime')}
					</button>

					{showCustomScheduleInput && (
						<div className={styles.scheduleCustomRow}>
							<input
								type="datetime-local"
								className={styles.scheduledInput}
								value={customScheduleValue}
								onChange={event => setCustomScheduleValue(event.target.value)}
								disabled={isRescheduling}
							/>
							<button
								type="button"
								className={styles.scheduleApplyBtn}
								disabled={isRescheduling || !customScheduleValue}
								onClick={() => {
									const parsed = parseDateTimeInputValue(customScheduleValue);
									if (parsed) {
										void handleApplyReschedule(editingItem.id, parsed);
									}
								}}
							>
								{t('schedule')}
							</button>
						</div>
					)}

					<div className={styles.scheduleEditorActions}>
						<button
							type="button"
							className={styles.scheduleSecondaryBtn}
							disabled={isRescheduling}
							onClick={() => {
								setEditingMessageId(null);
								setShowCustomScheduleInput(false);
								setEditContent('');
								setMenuPosition(null);
							}}
						>
							{t('cancel', { ns: 'common' })}
						</button>
						<button
							type="button"
							className={styles.schedulePrimaryBtn}
							disabled={isRescheduling || (showCustomScheduleInput && !customScheduleValue)}
							onClick={() => {
								void handleSaveChanges();
							}}
						>
							{t('saveChanges')}
						</button>
					</div>
				</div>,
				document.body
			)}
		</section>
	);
};

export default ScheduledMessagesPanel;
